import {
	EventKeys,
	EventValues,
	EventArgs,
	EventLabel,
	ErrorArgs,
	EventCategory,
	EventAction,
	FormErrors,
	Identifiers,
} from './ga-enums-interfaces';

import { DeepMap, FieldError } from 'react-hook-form';
import { IFormInput } from '../components/domains/forms/patient/enrollment.models';

import { window } from 'browser-monads';
import Analytics from './analytics';
import { Question } from '../components/hoc/context/product/context-model';
import { EnrollmentContextService } from '../components/hoc/context/enrollment/enrollment-context';
import { ContextService } from '../components/hoc/context/product/context';
import { getCustomKeyValues } from './analytics-helper';

export const replaceHtmlTags = (
	strWithHtmlTags: string,
	strTarget: string = ' '
) => {
	try {
		let resStr = strWithHtmlTags.replace(/<[^>]*>/g, strTarget);
		if (resStr) {
			resStr = resStr.trim();
		}
		return resStr;
	} catch (err) {
		return strWithHtmlTags;
	}
};

class GoogleAnalytics extends Analytics {
	event: string = EventValues.GAEvent;

	private static instance: GoogleAnalytics;

	public static getInstance(): GoogleAnalytics {
		if (!GoogleAnalytics.instance) {
			GoogleAnalytics.instance = new GoogleAnalytics();
		}

		return GoogleAnalytics.instance;
	}

	public sendSimpleEvent(
		category: string,
		action: string,
		label: string,
		pageRequestTime?: number
	) {
		if (pageRequestTime) {
			this.sendEvent({ [EventKeys.PageRequestTime]: pageRequestTime });
		}
		this.sendEvent({
			[EventKeys.EventCategory]: category,
			[EventKeys.EventAction]: action,
			[EventKeys.EventLabel]: label,
		});
	}

	public sendEvent(eventArgs: EventArgs): void {
		try {
			const dL = eventArgs.hasOwnProperty(EventKeys.PageRequestTime)
				? eventArgs
				: Object.assign(
						{},
						{ event: this.event, pageURL: `${window.location.href}` },
						eventArgs
				  );
			if (window) {
				const pageRequestTimeEvent = eventArgs.hasOwnProperty(
					EventKeys.PageRequestTime
				)
					? {
							[EventKeys.PageRequestTime]: eventArgs[EventKeys.PageRequestTime],
					  }
					: undefined;
				if (pageRequestTimeEvent) {
					window.dataLayer.push(pageRequestTimeEvent);
				}
				delete dL[EventKeys.PageRequestTime];
				if (Object.keys(dL).length) {
					window.dataLayer.push(dL);
				}
			}
		} catch (error) {
			console.error(error);
		}
	}

	public sendErrors(
		screen: string,
		errors: ErrorArgs | DeepMap<IFormInput, FieldError>
	): void {
		const errorsList: FormErrors[] = [];

		for (const key in errors) {
			const values = errors as { [key: string]: { message: string } };

			const eventArgs: FormErrors = {
				[EventKeys.Event]: this.event,
				[EventKeys.EventCategory]: screen,
				[EventKeys.EventAction]: EventAction.Error,
				[EventKeys.EventLabel]: `${key} - ${values[key].message}`,
				[EventKeys.ErrorForm]: screen,
				[EventKeys.ErrorField]: `${key}`,
				[EventKeys.ErrorName]: `${values[key].message}`,
				[EventKeys.PageURL]: `${window.location.href}`,
			};

			errorsList.push(eventArgs);
		}

		try {
			if (window) {
				window.dataLayer.push(...errorsList);
			}
		} catch (error) {
			console.error(error);
		}
	}

	public sendQuestionEvent(
		question: Question,
		choice: string | undefined,
		label: string,
		actualChoice: string | undefined,
		ctxt: ContextService,
		enrollmentCtxt: EnrollmentContextService
	): void {
		// TODO: move pageRequestTime to gatsby-route-change event
		ga.sendEvent({
			[EventKeys.PageRequestTime]: new Date().getTime(),
		});

		const customKeyValues = getCustomKeyValues(
			choice,
			actualChoice,
			question,
			ctxt,
			enrollmentCtxt
		);

		const additionalInfo = {
			...customKeyValues,
			[EventKeys.IsSelectedStatus]: choice !== undefined ? 'Success' : 'Error',
		};

		ga.sendEvent({
			[EventKeys.EventCategory]: EventCategory.SignUp,
			[EventKeys.EventLabel]: replaceHtmlTags(label, ''),
			...additionalInfo,
		});
	}
}

//TODO - make GoogleAnalytics singleton and remove ga
const ga = GoogleAnalytics.getInstance();

export default ga;
export {
	EventKeys,
	EventValues,
	EventLabel,
	EventCategory,
	EventAction,
	Identifiers,
	GoogleAnalytics,
};
